/**
 * Self-contained browser RUM beacon for static sites (marketing, docs) —
 * Cloudflare-Web-Analytics-style.
 *
 * Static sites cannot use @buildwithflux/telemetry-browser (they ship little
 * to no client JavaScript and cannot mint a Flux token), so this builds a
 * dependency-free inline script that collects Web Vitals (LCP/CLS/FCP/TTFB/
 * INP approximations via PerformanceObserver) plus ResourceTiming and
 * NavigationTiming entries, and posts a FlushPayload-shaped batch to
 * `POST /telemetry/perf` on the public site-token lane. The event shapes
 * mirror the telemetry-browser network/vitals plugins so the ingest worker
 * and Beam pipeline consume them unchanged.
 *
 * Vitals follow the standard measurement models: CLS uses session windows
 * (5s max, 1s gap) and reports the worst window; INP keeps per-interaction
 * longest durations and reports the high-percentile estimate (one interaction
 * skipped per 50, as in the web-vitals library).
 *
 * Batches flush per page path: on visibility-hidden/pagehide and, for
 * client-routed SPAs (docs), on history navigation — so every event batch is
 * attributed to the path it was measured on. A back/forward-cache restore
 * re-arms collection for the restored page.
 *
 * The flush rides `sendBeacon` with a text/plain Blob: text/plain keeps the
 * beacon a CORS "simple request" (no preflight) and sendBeacon survives page
 * teardown; the ingest worker parses the body as JSON regardless of content
 * type. A rejected beacon (quota) keeps the batch for the next flush trigger.
 */

/**
 * The inline-script source for the static-site RUM beacon. `endpoint` is the
 * ingest URL already carrying the site's public token (see rumSiteTokens.ts).
 */
export function buildRumBeaconSnippet(endpoint: string): string {
    const endpointLiteral = JSON.stringify(endpoint);
    return `(function () {
    if (!window.PerformanceObserver || !navigator.sendBeacon) return;
    var ENDPOINT = ${endpointLiteral};
    // Headroom below the queue cap is reserved for the vitals events appended
    // at flush time, so a resource-heavy page cannot crowd them out.
    var MAX_EVENTS = 500;
    var queue = [];
    var sessionId = randomId();
    var pagePath = location.pathname;

    function push(type, time, value, details) {
        if (queue.length >= MAX_EVENTS) return;
        queue.push(makeEvent(type, time, value, details));
    }

    function makeEvent(type, time, value, details) {
        var item = {type: type, time: time};
        if (typeof value === "number" && isFinite(value)) item.value = value;
        if (details) item.details = details;
        return item;
    }

    function randomId() {
        return window.crypto && crypto.randomUUID
            ? crypto.randomUUID()
            : String(Date.now()) + "-" + Math.random().toString(16).slice(2);
    }

    function browserId() {
        try {
            var key = "flux.rum.browserId";
            var id = localStorage.getItem(key);
            if (!id) {
                id = randomId();
                localStorage.setItem(key, id);
            }
            return id;
        } catch (e) {
            return undefined;
        }
    }

    function describeTarget(node) {
        if (!node || node.nodeType !== 1) return undefined;
        var id = node.id ? "#" + node.id : "";
        var className = typeof node.className === "string" ? node.className.trim() : "";
        var cls = className ? "." + className.split(/\\s+/).slice(0, 3).join(".") : "";
        return (node.tagName ? node.tagName.toLowerCase() : "") + id + cls;
    }

    function observe(type, options, handler) {
        try {
            if (
                !PerformanceObserver.supportedEntryTypes ||
                PerformanceObserver.supportedEntryTypes.indexOf(type) === -1
            )
                return;
            var observer = new PerformanceObserver(function (list) {
                handler(list.getEntries());
            });
            observer.observe(Object.assign({type: type, buffered: true}, options));
        } catch (e) {
            // Unsupported entry type — skip
        }
    }

    // Resource + navigation timing: same event shape as the app's
    // telemetry-browser network plugin (type/time/value/details).
    observe("resource", {}, function (entries) {
        for (var i = 0; i < entries.length; i++) {
            push("network.resource", entries[i].startTime, entries[i].duration, entries[i].toJSON());
        }
    });
    observe("navigation", {}, function (entries) {
        for (var i = 0; i < entries.length; i++) {
            push("network.navigation", entries[i].startTime, entries[i].duration, entries[i].toJSON());
            if (entries[i].responseStart > 0) {
                push("vitals.ttfb", entries[i].responseStart, entries[i].responseStart);
            }
        }
    });
    observe("paint", {}, function (entries) {
        for (var i = 0; i < entries.length; i++) {
            if (entries[i].name === "first-contentful-paint") {
                push("vitals.fcp", entries[i].startTime, entries[i].startTime);
            }
        }
    });

    // LCP accumulates until first flush; it only applies to the initial load.
    var lcp;
    observe("largest-contentful-paint", {}, function (entries) {
        var last = entries[entries.length - 1];
        if (last) lcp = last;
    });

    // CLS session-window model: shifts group into windows capped at 5s with a
    // 1s gap; the reported value is the worst window (standard CLS).
    var clsWorst = 0;
    var clsWindow = 0;
    var clsWindowStart = 0;
    var clsWindowLast = 0;
    observe("layout-shift", {}, function (entries) {
        for (var i = 0; i < entries.length; i++) {
            var entry = entries[i];
            if (entry.hadRecentInput) continue;
            if (
                clsWindow > 0 &&
                entry.startTime - clsWindowLast < 1000 &&
                entry.startTime - clsWindowStart < 5000
            ) {
                clsWindow += entry.value;
            } else {
                clsWindow = entry.value;
                clsWindowStart = entry.startTime;
            }
            clsWindowLast = entry.startTime;
            if (clsWindow > clsWorst) clsWorst = clsWindow;
        }
    });

    // INP: longest duration per interaction, reporting the high-percentile
    // estimate (skip one longest interaction per 50) as in web-vitals.
    var interactions = {};
    var interactionMeta = {};
    var interactionCount = 0;
    observe("event", {durationThreshold: 40}, function (entries) {
        for (var i = 0; i < entries.length; i++) {
            var entry = entries[i];
            if (!entry.interactionId) continue;
            if (!(entry.interactionId in interactions)) {
                interactionCount++;
                interactions[entry.interactionId] = 0;
            }
            if (entry.duration > interactions[entry.interactionId]) {
                interactions[entry.interactionId] = entry.duration;
                interactionMeta[entry.interactionId] = {name: entry.name, target: describeTarget(entry.target)};
            }
        }
    });

    function inpCandidate() {
        var ids = Object.keys(interactions);
        if (ids.length === 0) return undefined;
        ids.sort(function (a, b) {
            return interactions[b] - interactions[a];
        });
        var index = Math.min(ids.length - 1, Math.floor(interactionCount / 50));
        return {id: ids[index], value: interactions[ids[index]]};
    }

    function resetVitals() {
        lcp = undefined;
        clsWorst = 0;
        clsWindow = 0;
        interactions = {};
        interactionMeta = {};
        interactionCount = 0;
    }

    // Batches whose sendBeacon() call was rejected (pending-beacon quota);
    // each is retried on later flush triggers with its original path intact.
    var pendingPayloads = [];
    var MAX_PENDING = 4;

    function flush() {
        var now = performance.now();
        var events = queue.slice();
        if (lcp) events.push(makeEvent("vitals.lcp", lcp.startTime, lcp.startTime));
        if (clsWorst > 0) events.push(makeEvent("vitals.cls", now, clsWorst));
        var inp = inpCandidate();
        if (inp && inp.value > 0) {
            var inpMeta = interactionMeta[inp.id];
            events.push(
                makeEvent("vitals.inp", now, inp.value, inpMeta ? {eventName: inpMeta.name, target: inpMeta.target} : undefined),
            );
        }
        if (events.length > 0) {
            var payload = {
                data: events,
                timeOrigin: new Date(performance.timeOrigin).toISOString(),
                // Tag values are strings (the ingest sanitizer drops non-strings);
                // undefined entries are omitted by JSON.stringify.
                tags: {
                    "browser.id": browserId(),
                    "telemetry.sessionId": sessionId,
                    "telemetry.location": pagePath,
                    "telemetry.userAgent.cpu.cores":
                        navigator.hardwareConcurrency ? String(navigator.hardwareConcurrency) : undefined,
                    "telemetry.userAgent.memory.total": navigator.deviceMemory ? String(navigator.deviceMemory) : undefined,
                    "telemetry.userAgent.connection.effectiveType":
                        navigator.connection && navigator.connection.effectiveType
                            ? String(navigator.connection.effectiveType)
                            : undefined,
                },
            };
            // The active batch always joins the retry queue (so a terminal
            // pagehide flush can never strand it); when the queue is over
            // capacity the oldest batches are evicted, bounding memory while
            // keeping the newest telemetry.
            pendingPayloads.push(JSON.stringify(payload));
            while (pendingPayloads.length > MAX_PENDING) pendingPayloads.shift();
            queue = [];
            resetVitals();
        }
        var unsent = [];
        for (var i = 0; i < pendingPayloads.length; i++) {
            if (!navigator.sendBeacon(ENDPOINT, new Blob([pendingPayloads[i]], {type: "text/plain"}))) {
                unsent.push(pendingPayloads[i]);
            }
        }
        pendingPayloads = unsent;
    }

    // SPA route change (docs): flush the finished route's batch under its own
    // path, then start a fresh batch for the new route.
    function onRouteChange() {
        if (location.pathname === pagePath) return;
        flush();
        pagePath = location.pathname;
    }
    if (window.history && history.pushState) {
        var pushState = history.pushState;
        history.pushState = function () {
            var result = pushState.apply(this, arguments);
            onRouteChange();
            return result;
        };
        var replaceState = history.replaceState;
        history.replaceState = function () {
            var result = replaceState.apply(this, arguments);
            onRouteChange();
            return result;
        };
        window.addEventListener("popstate", onRouteChange);
    }

    document.addEventListener("visibilitychange", function () {
        if (document.visibilityState === "hidden") flush();
    });
    window.addEventListener("pagehide", flush);
    // Back/forward-cache restore: the page lives again, keep collecting and
    // flush whatever accumulates on the next lifecycle transition.
    window.addEventListener("pageshow", function (event) {
        if (event.persisted) resetVitals();
    });
})();`;
}
